// External modules
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone'
import { momentLocalizer } from 'react-big-calendar'
import {cloneDeep} from 'lodash'
import { RRule } from 'rrule'
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Internal modules
import { checkOverlapping } from '../../../lib/overlap';
import { isJsonEqual } from '../../../lib/typeCheck'; 
import { CalendarItems } from '../../../lib/calendar-items';

// Store
import { focusSchedule, selectFilteredSchedules } from '../../../store/rules/ruleSchedulesSlice';
import { getSelectedNodes } from '../../../store/positions/menuNodesSlice';
import { getSettings } from '../../../store/rules/settingsSlice';
import { ScheduleStyle } from '../../../store/rules/types';
import { selectFilteredRules } from '../../../store/rules/rulesSlice';
import { createCompleteRule } from '../../../store/rules/rules';
import { fetchWrxOptions, getAllWrxOptions } from '../../../store/rules/wrxOptionSlice';
import { getOverlapWarning, resetWarning, addWarning, editWarning } from '../../../store/common/warningSlice';

// Internal components
import { EditPanel } from './EditPanel';
import { colour_lookup } from '../../common/Colours';
import { CustomEvent, CustomEventWrapper } from './Calendar';
import { WarningPopUp } from '../../common/fields/devices/PopUp';

// Common fields 
import { StandardMainDisplayMessage } from '../../common/fields/devices/DisplayMessages';
import { messages_common, messages_audio } from '../../common/types/Messages';
import { Header, Toolbar } from '../../common/fields/devices/Calendar';

// Styled components
import { StyledContainer, StyledMiddleContainer, StyledRightContainer } from '../../common/styles/devices/Container';
import { StyledBigCalendar } from '../../common/styles/devices/Calendar';
import { StyledPopupModal } from '../../common/styles/devices/Container';

export function RawAudioConinuous({ path: string }) {
  
  const dispatch = useDispatch();
  const selectedNodes = useSelector(getSelectedNodes);
  // useEffect(() => { dispatch(fetchStation(5)) }, []) 
  // get the wrx_options
  const wrxOptions = useSelector(getAllWrxOptions);  
  useEffect(() => {
    if (!wrxOptions.length){
      dispatch(fetchWrxOptions())
    }    
  })
  const defaultWrxOption = wrxOptions.filter(item => item.description === 'Continuous').map(item => item.wrx_option_id)
 
  // get the rules
  const rules = useSelector(selectFilteredRules('rawaudio'), isJsonEqual);
  
  const scheduleIds = rules.map(rule => rule.schedules_id);

  // Retrieve the state of overlapping warning from the store
  const warning = useSelector(getOverlapWarning);
  
  // Handles the state of the `warning pop up modal`, keep the default state to true
  const [isWarningOpen, setisWarningOpen] = useState(true);
  const toggleMainLimitPopup = () => {
    setisWarningOpen(!isWarningOpen);
  }

  // Monitors the state of the popup modal
  useEffect(() => {
    // Checks if the pop up modal is open
    if(!isWarningOpen){
      
      // Checks if there are existing overlaps with the current overlap
      if(isOverlapping.state){    
        // Update the store to add the number of attempts
        dispatch(editWarning());
      }
      // Checks if there aren't any overlaps and the store has a record of overlapping attempts
      if(!isOverlapping.state && warning.overlap.rawaudio && warning.attempt >= 1){     
        // Update the store to clear   
        dispatch(resetWarning());
      }
            
      setisWarningOpen(!isWarningOpen);
    }
  }, [isWarningOpen])
  
  // Get the schedules and filter for raw audio rules fetched above
  const calendar_items = useSelector(selectFilteredSchedules(), isJsonEqual)
    .filter(item => scheduleIds.includes(item.schedules_id))
    .map(item => ({ ...item, ruleTitle: rules.filter(rule => rule.schedules_id === item.schedules_id)[0].title}));
  const focusedSchedule = calendar_items.filter(schedule => schedule.focus)[0] || null;
  const globalSettings = useSelector(getSettings);

  let calendar_items_recurring = [];
  
  calendar_items.forEach((item) => {
    calendar_items_recurring = CalendarItems(item, calendar_items_recurring)
  });
  
  // Checks if existing schedules have overlaps with changes
  const isOverlapping = checkOverlapping(calendar_items_recurring);

  // Checks if the user is trying to change an existing schedules also if it overlaps for the firstime 
  if(selectedNodes.length > 0 && isOverlapping.state && !warning.overlap.rawaudio && warning.attempt == 0){
    // Update the store to alert the user  
    dispatch(addWarning({id: parseInt(selectedNodes[0].id), overlap: {rawaudio: true}, attempt: warning.attempt + 1, existing: true}));
  }
  // If an existing overlap, finally get rid of its problem, update the stroe to clear its state
  if(selectedNodes.length > 0 && !isOverlapping.state && warning.existing){
    dispatch(resetWarning());
  }
  
  calendar_items_recurring.forEach((item) => {
    item.overlapping = false;
    
    isOverlapping.schedules.forEach((overlapping_id) => {
      if (overlapping_id === item.id) {
        item.overlapping = true;
      }
    })
  })
  
  moment.locale('gb', {
      week: {
          dow: 1
      },
  });

  const localizer = momentLocalizer(moment) // or globalizeLocalizer
  
  let formats = {
    timeGutterFormat: 'HH:mm'
  }
  
  function log_event_select(slotInfo) {
    console.log(slotInfo);
    console.log({dtstart: moment(slotInfo.start).format('YYYY-MM-DD HH:mm:ss'), duration: (slotInfo.end.getHours() - slotInfo.start.getHours())*3600, byweekday: [slotInfo.start.getDay()-1]});
  }
  
  function convertJsToRruleDays(js_day) {
    const rrule_days = [6, 0, 1, 2, 3, 4, 5];
    return rrule_days[js_day]
  }
  
  function setFocusBorders(props) {
    
    const style: ScheduleStyle = {background: props.style.background}
    let line_type = 'solid'
    
    if (!props.saved) {
      line_type = `dashed`;
    }
    
    if (props.focus) {
      if (props.saved) {
        style.border = `3px solid  ${props.style.border}`;
      } else {
        style.border = `3px dashed  ${props.style.border}`;
      }
    } else {
      if (props.saved) {
        style.border = `1px solid  ${props.style.border}`;
      } else {
        style.border = `2px dashed  ${props.style.border}`;
      }
    }
    
    if (props.overlapping) {
      style.border = `3px solid ${colour_lookup('red')}`;
    }
    
    return {style: style}
  }  

  // Checks if a station has the capability attribute
  const has_capability = selectedNodes.length > 0 && selectedNodes[0].features.use_rawaudio_schedules !== null; 
  
  // Check if the instrument_type is Noise and sub_type is Octave or Leq
  const has_instrument_type = selectedNodes.length > 0 && (selectedNodes[0].instrument_type == 1 && (selectedNodes[0].instrument_subtype == 0 || selectedNodes[0].instrument_subtype == 1));
  
  return (
      <StyledContainer>
        <StyledMiddleContainer>          
          {selectedNodes.length == 0 ? <StandardMainDisplayMessage message={messages_common.select}/> : null}
          {selectedNodes.length > 0 && !has_capability && has_instrument_type? <StandardMainDisplayMessage message={messages_audio.not_capable}/> : null}
          {selectedNodes.length > 0 && !has_instrument_type? <StandardMainDisplayMessage message={messages_audio.no_service}/> : null}
          {selectedNodes.length > 0 && !selectedNodes[0].features.use_rawaudio_schedules && has_capability && has_instrument_type? <StandardMainDisplayMessage message={messages_audio.disabled}/> : null}          
          {selectedNodes.length > 0 && selectedNodes[0].features.use_rawaudio_schedules && has_capability && has_instrument_type? 
            <StyledBigCalendar
              padding='75px 5% 20px 5%'
              min_height='758px'
              background='#e8bbbb !important'
              localizer={localizer}
              events={calendar_items_recurring}
              startAccessor="dtstart"
              endAccessor="end"
              defaultView='week'
              showMultiDayTimes={true}
              step={60}
              timeslots={1}
              formats={formats}
              resizable={true}
              popup={true}
              components={{
                day: { header: Header },
                week: { header: Header },
                month: { header: Header },
                toolbar: Toolbar,
                event: CustomEvent,
                eventWrapper: CustomEventWrapper
              }}
              selectable={true}
              // onSelectSlot={log_event_select}
              onSelectSlot={(slotInfo) => dispatch(createCompleteRule(
                { 
                  title: 'Recording Audio',
                  dtstart: moment(slotInfo.start).format('YYYY-MM-DD HH:mm:ss'), 
                  duration: (slotInfo.end.getHours() == 23)? (slotInfo.end.getHours() - slotInfo.start.getHours() + 1)* 3600 :(slotInfo.end.getHours() - slotInfo.start.getHours())*3600,
                  byweekday: [convertJsToRruleDays(slotInfo.start.getDay())], 
                  category: 'rawaudio' 
                }, 
                {
                  id: null,
                  account_id: 1,
                  title: 'Recording Audio',
                  category: 'rawaudio',
                  wrx_option_id: defaultWrxOption[0]                  
                },
                selectedNodes,
                calendar_items_recurring,
                warning  
              ))}
              onSelectEvent={(event, e) => dispatch(focusSchedule({id: event.id}))}
              
              // onSelectEvent={log_event_select}
              eventPropGetter={setFocusBorders}
            />
          : null}
          
        </StyledMiddleContainer>
        {/* If there is an over lap for the first time, open the warning modal to alert the user */}
        {warning.overlap.rawaudio === true ? (isWarningOpen && warning.attempt <=1) && 
        <StyledPopupModal>
          <WarningPopUp handleClose={toggleMainLimitPopup}/>
        </StyledPopupModal> : null}
        <StyledRightContainer>          
          {focusedSchedule != null && selectedNodes.length > 0 && selectedNodes[0].features.use_rawaudio_schedules && has_capability?
          <EditPanel schedule={focusedSchedule} schedules={calendar_items} rules={rules} isOverlapping={isOverlapping}/>
          : null}
        </StyledRightContainer>
      </StyledContainer>
  );
}